<template>
  <div class="mol-center-card-width">

    <!-- cannot display profile -->
    <div v-if="errorMessage" class="card profile-card-new">
      <div class="card-header">
        <div class="placeholder-profile-image"></div>
        <div class="placeholder-profile-name"></div>

      </div>
      <div class="card-body">
        <div class="error-message">{{errorMessage}}</div>
      </div>
    </div>

    <!-- profile card wrapper -->
    <div v-else-if="profileUser" class="profile-card-wrapper-new">

      <!-- membership ribbon -->
      <div v-if="profileUser.membershipType" class="ribbon">
        <span class="ribbon-msg"
              data-bs-toggle="tooltip"
              data-bs-placement="right"
              data-bs-custom-class="member-banner-tooltip"
              data-bs-offset="0,0"
              title="member">
          <img :src="`${$imagePathBase}logo-white.svg`"/>
        </span>
      </div>

      <!-- profile card -->
      <div class="card profile-card-new">

        <!-- header with name and image -->
        <div class="card-header">

          <ProfileFilmsViewedGraphic class="profile-films-viewed-graphic" :viewed_array=profileUser.viewed width="100%" height="100%" color="#decc8280"></ProfileFilmsViewedGraphic>
          <div class="profile-info-layered-over-graphic">
            <ProfilePicture :user=profileUser :size="100" :editable="true"></ProfilePicture>
            <ProfileName class="name-text" :user="profileUser"></ProfileName>
            <div ref="percentageStat" class="percentage-stat">{{percentageViewed}}% viewed</div>
          </div>

          <div v-if="isMyProfile" class="share-button" @click="showShareProfileModal">
            <i class="fa fa-share-alt"/>
          </div>

          <div v-else class="relationship-icons">
            <img v-if="isMyFriend"
               class="relationship-icon-friend"
               :src="`${$imagePathBase}friend-pic-default.png`"
               data-bs-toggle="tooltip"
               data-bs-custom-class="relationship-friend-tooltip"
               data-bs-placement="left"
               data-bs-offset="0,10"
               title="friend"
            />
            <img v-if="clubsWeShareMembershipIn?.length"
               class="relationship-icon-clubfellow"
               :src="`${$imagePathBase}default-club-pic.png`"
               data-bs-toggle="tooltip"
               data-bs-custom-class="relationship-tooltip"
               data-bs-placement="left"
               data-bs-offset="0,10"
               data-bs-html="true"
               :title="`<b>member of</b><br>${clubsWeShareMembershipIn.join('<br>')}`"
            />
            <img v-if="sharesSubscriptionWithMe"
               class="relationship-tooltip"
               :src="`${$imagePathS3}products/family-membership-icon.svg`"
               data-bs-toggle="tooltip"
               data-bs-custom-class="member-banner-tooltip"
               data-bs-placement="left"
               data-bs-offset="0,10"
               title="shared membership"
            />
          </div>
        </div>

        <!-- various detail cards -->
        <div class="card-body">

          <!-- category complete achievements card -->
          <ExpandableCard class="profile-detail-card achievements-card">
            <template #header>
              <section class="expandable-card-header">
                <div class="title">{{categoryCompleteAchievementHeaderText}}</div>
                <div class="subtitle">Awarded for viewing every film in a category for a given year.</div>
              </section>
            </template>

            <template #body>
              <LoadingSpinner v-if="!categoryCompleteAchievements" class="text-secondary" style="margin-top: 10px;"></LoadingSpinner>
              <span v-else>
                <AchievementRing
                    v-for="(achievement, index) in categoryCompleteAchievements"
                    :key="index"
                    :total_segments="achievement.numFilms"
                    :filled_segments="achievement.numFilms"
                    :tooltip_text="achievement.tooltipHtml"
                    size="8%"
                    :center_img="achievement.icon"
                    :display_text='`<b style="color: var(--mol-color-gold);">${achievement.displayText}</b>`'
                    class="profile-achievement-ring"
                ></AchievementRing>
              </span>
            </template>

            <template #footer>
              <router-link v-if="isMyProfile && !userStore.isFullMember()" to="/membership" class="membership-required-link">
                <img :src="`${$imagePathBase}logo-red-carpet.svg`"/>
                <div class="membership-required-text">
                  Members see their completion rings<br>
                  for <i>all</i> ceremonies. <b>Learn more <sup><i class="fa fa-external-link"></i></sup></b>
                </div>
              </router-link>
            </template>
          </ExpandableCard>

          <!-- films viewed card -->
          <ExpandableCard class="profile-detail-card films-viewed-card-new">
            <template #header>
              <section class="expandable-card-header">
                <div class="title">{{filmHeaderText}}</div>
              </section>
            </template>

            <template #body>
              <LoadingSpinner v-if="!filmsViewed" class="text-secondary" style="margin-top: 10px;"></LoadingSpinner>
              <div v-else v-for="decade in filmDecadeMap.keys()" :key="decade" class="decade">
                <b>{{decade}}s</b>
                <div class="film-list">
                  <div v-for="film in filmDecadeMap.get(decade)" :key="film._id" >
                    <FilmLink
                        :film_name="film.name"
                        :film_id="film._id"
                        single_line="true"
                        :link_location="`User Profile: ${user_id}`"
                        no_strikethrough="true"
                        :details_modal_disabled="!userStore.isLoggedIn()">
                    </FilmLink>
                  </div>
                </div>
              </div>
            </template>
          </ExpandableCard>

        </div>
      </div>
    </div>

  </div>


  <GenericModal v-if="show_share_profile_modal" @modal-closed="show_share_profile_modal=false">
    <template #header>
      <h6>share profile</h6>
    </template>

    <div class="share-profile-modal-body">

      <div class="profile-sharing-link" :class="{'private-profile': !profileUser.publicProfile}">
        <QRCodeVue3 :value="fullProfileLink"
                    :width="250" :height="250"
                    :image="$imagePathBase + 'logo-goldtext.png'"
                    :imageOptions="{margin: 2, imageSize: 0.25}"
                    :dotsOptions="{type: 'extra-rounded', color: '#b49823ff'}"
                    :cornersSquareOptions="{ color: '#b49823ff' }"
                    :cornersDotOptions="{ color: '#b49823ff' }"
                    :backgroundOptions="{ color: 'transparent' }"

                    :qrOptions="{ typeNumber: 0, mode: 'Byte', errorCorrectionLevel: 'H' }"
                    myclass="profile-sharing-link-qr-code"
        />
        <button
            class="btn btn-sm btn-block btn-mol btn-copy-profile-link"
            type="button"
            @click="copyProfileLinkToClipboard"
            :disabled="!profileUser.publicProfile"
        >
          <span v-if="!profile_link_copied"><i class="fa fa-clipboard" aria-hidden="true"></i> copy link to clipboard
<!--            <span class="profile-sharing-link-url">{{profileLink}}</span>-->
          </span>
          <span v-else>copied!</span>
        </button>
      </div>

      <div v-if="!profileUser.publicProfile" class="profile-settings">
        <p><b>Brag about your accomplishments!</b></p>
        <p>Allow anyone to see your viewed films and achievements by enabling your profile to be seen publicly*.</p>
        <p>
          <button class="btn btn-sm btn-mol btn-cta" type="button" @click="togglePublicProfile">
            <i class="fa fa-bullhorn"/> make it sharable
          </button>
        </p>
        <p>You can toggle this setting any time on the settings page.</p>
        <div class="public-profile-footnote">
          * Personal account information, like your email, is <u>never</u> shared with <u>anyone</u>.<br>
        </div>
      </div>


    </div>

  </GenericModal>

</template>


<script>
import axios from "axios";
import ProfilePicture from "@/components/profile/ProfilePicture.vue";
import ProfileName from "@/components/profile/ProfileName.vue";
import ProfileFilmsViewedGraphic from "@/components/profile/ProfileFilmsViewedGraphic.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import ExpandableCard from "@/components/ExpandableCard.vue";
import FilmLink from "@/components/FilmLink.vue";
import analytics from "@/analytics";
import {userStore} from "@/userStore";
import AchievementRing from "@/components/achievements/AchievementRing.vue";
import {Film} from "@/film";
import GenericModal from "@/components/modal/GenericModal.vue";
import QRCodeVue3 from "qrcode-vue3";

export default {
  name: "ProfileCard",
  // eslint-disable-next-line vue/no-unused-components
  components: {
    QRCodeVue3, GenericModal,
    AchievementRing,
    FilmLink, ExpandableCard, LoadingSpinner, ProfileFilmsViewedGraphic, ProfileName, ProfilePicture},
  props: ['user_id'],
  computed: {
    profileLink() {
      return `/profile/...${this.$route.path.slice(-6)}`;
    },
    fullProfileLink() {
      return `${this.$apiPath}/profile/${this.user_id}`;
    },
    userStore() {
      return userStore
    },
    isMyProfile() {
      return (this.user_id === userStore.getUserId());
    },
    isMyFriend() {
      return this.profileRelationships?.includes('friend');
    },
    sharesSubscriptionWithMe() {
      return this.profileRelationships?.includes('shared-subscription');
    },
    clubsWeShareMembershipIn() {
      return this.profileRelationships?.reduce((accClubNames, curRelationship) => {
        if ( curRelationship.startsWith('clubfellow:') ) {
          return [...accClubNames, curRelationship.slice(curRelationship.indexOf(':')+1)];
        }
        return accClubNames;
      }, []) || [];
    },
    percentageViewed() {
      if ( !(this.filmsViewed?.length) ) {
        return 0; // 0% does not include decimals
      }
      if ( this.filmsViewed.length === this.totalFilmCount ) {
        return 100; // 100% does not include decimals
      }
      return ((this.filmsViewed.length / this.totalFilmCount) * 100).toFixed(2);
    },
    filmHeaderText() {
      const numFilmsViewed = this.profileUser.viewed.length;
      return (
        (numFilmsViewed === 0)? 'no films viewed yet' :
            (numFilmsViewed === 1)? '1 film viewed' :
                `${numFilmsViewed} films viewed`
      );
    },
    categoryCompleteAchievementHeaderText() {
      return (
          !this.categoryCompleteAchievements? 'categories complete' :
              (this.categoryCompleteAchievements.length === 0)? 'no categories complete yet' :
                  (this.categoryCompleteAchievements.length === 1)? '1 category complete' :
                      `${this.categoryCompleteAchievements.length} categories completed`
      );
    },
  },
  data() {
    return {
      profileUser: null,
      profileRelationships: null,
      errorMessage: null,
      filmsViewed: null,
      filmDecadeMap: new Map(),
      totalFilmCount: null,
      categoryCompleteAchievements: null,

      show_share_profile_modal: false,
      profile_link_copied: false,
    }
  },
  watch: {
    user_id(/*newValue, oldValue*/) {
      console.log('user_id updated')
      this.refreshUser();
    },
  },
  async created() {
    return this.refreshUser();
  },

  methods: {

    async refreshUser() {
      try {

        // reset user data
        this.profileUser = null;
        this.profileRelationships = null;
        this.errorMessage = null;
        this.filmsViewed = null;
        this.filmDecadeMap = new Map();
        this.categoryCompleteAchievements = null;

        // try to get the user
        const profileUserResponse = await axios.get(`/api/user/profile/${this.user_id}`, {
          validateStatus: status => (status >= 200 && status < 300) || (status === 401) || (status === 404) // handle expected 401s and 404s
        });

        if ( profileUserResponse.status === 404 ) {
          this.errorMessage = 'User not found.';
          return;
        }

        if ( profileUserResponse.status === 401 ) {
          this.errorMessage = `User's profile is not public.`;
          return;
        }

        this.profileUser = profileUserResponse.data.user;
        this.profileRelationships = profileUserResponse.data.relationships;

        // populate section cards
        await this.populateFilmsViewedSection(this.profileUser.viewed);
        await this.populateAchievementSection(this.profileUser._id);

      } catch (err) {
        this.errorMessage = 'Error loading profile.';
        console.error(`error loading profile ${this.user_id} : ${err}`)
      }
    },

    async populateFilmsViewedSection(_film_ids) {

      return Promise.all([
        axios.post('/api/film/names', { film_ids: _film_ids }),
        this.totalFilmCount || axios.get(`/api/film/count`).then(res => res.data), // no need to do this more than once
      ])
          .then(([namesResponse, filmCount]) => {

            //console.log(response);
            this.filmsViewed = namesResponse.data;
            this.totalFilmCount = filmCount;

            this.filmsViewed.sort((filmA, filmB) => {
              return Number(filmB.year) - Number(filmA.year);
            });

            this.filmsViewed.forEach((film) => {
              const decade = Math.floor(Number(film.year) / 10) * 10;
              //console.log(decade);

              let films = this.filmDecadeMap.has(decade) ? this.filmDecadeMap.get(decade) : [];
              films.push(film);
              this.filmDecadeMap.set(decade, films);
            });

            this.$refs.percentageStat?.classList.add('fade-in');
          })
          .catch(err => {
            this.filmsViewed = [];
            analytics.notifyBugsnagOfError(err);
          });

    },

    async populateAchievementSection(_user_id) {

      return axios.get(`/api/achievements/accomplished/${_user_id}`)
          .then(achievementsResponse => {

            //console.log(ceremonyCountResponse);

            this.categoryCompleteAchievements = achievementsResponse.data
                .filter(achievement => achievement.type === 'CATEGORY_COMPLETE')
                .map(achievement => {
                  achievement.icon = Film.getCategoryAchievementImage(achievement.normalizedCategory);
                  achievement.displayText = Film.getCeremonyYearText(achievement.ceremony);

                  if (achievement.normalizedCategory.toUpperCase() === 'HONORARY AWARD') {
                    achievement.tooltipHtml =
                        `Viewed each film nominated for a<br>
                      <b>Special or Honerary Award</b><br>
                      in the <b>${Film.getCeremonyOrdinalText(achievement.ceremony)} ceremony</b><br>
                      (honoring the films of ${Film.ceremonyYearsHonoredText(achievement.ceremony)})`;

                  } else {
                    achievement.tooltipHtml =
                        `Viewed each film nominated for<br>
                      <b>${achievement.category}</b><br>
                      in the <b>${Film.getCeremonyOrdinalText(achievement.ceremony)} ceremony</b><br>
                      (honoring the films of ${Film.ceremonyYearsHonoredText(achievement.ceremony)})`;
                  }
                  return achievement;
                });
          })
          .catch(err => {
            analytics.notifyBugsnagOfError(err);
          });
    },

    async togglePublicProfile() {

      if ( !this.profileUser ) {
        return;
      }
      const newSettingParam = this.profileUser.publicProfile ? 'false' : 'true';
      return axios.post(`/api/user/set-profile-pubic/${newSettingParam}`)
          .then(response => {
            userStore.setUser(response.data);

            // instead of calling refreshUser() we just update the profileUser directly
            this.profileUser = response.data;
          });
    },

    showShareProfileModal() {
      this.show_share_profile_modal = true;
      this.profile_link_copied = false;
    },
    copyProfileLinkToClipboard() {
      navigator.clipboard.writeText( this.fullProfileLink );
      this.profile_link_copied = true;
    }
  }

}
</script>

<style>
.member-banner-tooltip {
  /* OVERRIDABLE BOOTSTRAP VARIABLES
  --bs-tooltip-zindex
  --bs-tooltip-max-width
  --bs-tooltip-padding-x
  --bs-tooltip-padding-y
  --bs-tooltip-margin
  --bs-tooltip-color
  --bs-tooltip-bg
  --bs-tooltip-border-radius
  --bs-tooltip-opacity
  --bs-tooltip-arrow-width
  --bs-tooltip-arrow-height
   */

  /*--bs-tooltip-bg: var(--mol-color-red-carpet-slightly-faded)!important;*/
  /*--bs-tooltip-bg: #b49823!important;*/
  --bs-tooltip-bg: #910707 !important;
  --bs-tooltip-color: white!important;

  --bs-tooltip-opacity: 1.0!important;
  --bs-tooltip-arrow-height: 10px!important;
  --bs-tooltip-arrow-width: 10px!important;
  font-size: x-small!important;
}

.relationship-tooltip {
  --bs-tooltip-bg: #b49823!important;
  --bs-tooltip-color: var(--bs-body-bg)!important;

  --bs-tooltip-opacity: 1.0!important;
  --bs-tooltip-arrow-height: 10px!important;
  --bs-tooltip-arrow-width: 10px!important;
  font-size: x-small!important;
}

.relationship-friend-tooltip {
  --bs-tooltip-bg: var(--mol-friends-color)!important;
  --bs-tooltip-color: var(--mol-friends-bg)!important;

  --bs-tooltip-opacity: 1.0!important;
  --bs-tooltip-arrow-height: 10px!important;
  --bs-tooltip-arrow-width: 10px!important;
  font-size: x-small!important;
}
</style>


<style>
.share-profile-modal-body {
  text-align: center;
  position: relative;

  /* the link with the qr code and copy button */
  .profile-sharing-link {
    &.private-profile {
      filter: blur(5px);
    }

    .profile-sharing-link-qr-code {
    }

    .btn-copy-profile-link {
      margin-top: 20px;
      width: 230px;
      background-color: var(--mol-color-gold-text);
      color: var(--mol-color-gold-background);
      font-weight: 500;

      .profile-sharing-link-url {

      }
    }
  }


  /* the profile settings, shown to users with private profiles */
  .profile-settings {
    color: var(--bs-body-color);
    font-size: small;
    position: absolute;
    top: 20px;
    background-color: var(--mol-color-gold-background);
    padding: 1em;
    width: 100%;
    padding-bottom: 2px;

    border-style: solid;
    border-width: thin;
    border-radius: 5px;

    box-shadow: 0 3px 10px -5px grey;

    b {
      font-weight: 500;
    }

    .public-profile-footnote {
      color: var(--bs-tertiary-color);
      margin: 40px -5px 5px auto;
      text-align: right;
      width: 80%;
      font-size: 80%;
      font-style: italic;
      line-height: 110%;
    }

    .btn-cta {
      background-color: var(--mol-color-gold-text);
      color: var(--mol-color-gold-background);
    }
  }


}
</style>

<style scoped>



.profile-card-wrapper-new {
  position: relative;
}

.profile-card-new {
  background-color: var(--mol-color-gold-background);

  /*isolation: isolate;*/
  overflow: hidden;
  text-align: center;

  .card-header {
    position: relative;
    padding: 30px 0 10px 0;
    margin-left: 16px;
    margin-right: 16px;
    background-color: transparent;
    /*border-style: none;*/
    border-color: #decc8280;

    .share-button {
      position: absolute;
      top: 8px;
      right: -8px;
      color: var(--mol-color-gold-text);

      border-style: solid;
      border-width: thin;
      border-color: var(--mol-color-gold-text);
      border-radius: 5px;

      height: 16px;
      width: 16px;

      font-size: 10px;
      text-align: center;

      .fa {
        position: relative;
        left: -0.3px;
      }

      &:hover {
        color: black;
        background-color: var(--mol-color-gold-faded);
        cursor: pointer;
      }
    }

    .relationship-icons {
      /*background-color: lightpink;*/

      position: absolute;
      top: 0;
      right: -16px;
      padding-right: 4px;


      .public-profile-icon-and-link {
        color: var(--mol-color-gold-text);

        .public-profile-link {
          position: relative;
          font-size: xx-small;
          margin-right: 0.2em;
          top: -0.225em;
          opacity: 70%;
        }

        .public-profile-icon {
          font-size: 16px;
          opacity: 50%;
        }
      }




      img {
        opacity: 50%;
        height: 16px;
        margin: 0 2px 0 2px;

        &.relationship-icon-friend {
          border-radius: 50%;
        }

        &.relationship-icon-clubfellow {
          border-radius: 20%;
        }

      }

    }

  }

  .card-body {
    padding-top: 0;
    padding-bottom: 0;
    /*background-color: #decc8280;*/
  }
}

.name-text {
  display: block;
  margin-top: 0.5em;
  font-weight: 600;
  font-size: x-large;
}

.profile-detail-card {

  .card-header .expandable-card-header {

    .title {
      font-size: calc(1.275rem + .3vw); /* h4 */
      font-weight: 500;
    }
    .subtitle {
    }
  }

  background-color: var(--bs-body-bg);
  margin-top: 20px;
  margin-bottom: 20px;
}

.films-viewed-card-new {

  .expandable-card-header {
    position: relative;

    /* subtitle is the percentage viewed stat */
    .subtitle {
      padding: 0.225em 0 0.225em 0;
      font-family: 'Playpen Sans', cursive, sans-serif;
      color: var(--mol-color-gold-text);
      text-shadow: 0 0 10px var(--bs-body-bg);

      &.no-films-viewed {
        color: var(--bs-tertiary-color);
      }
    }
  }

  .decade {
    border: none;

    /* all but the first 'decade' will have a line on top */
    ~.decade {
      border-style: solid none none none;
      border-width: 1px;
      border-color: var(--bs-card-border-color);
      padding-top: 0.5em;
      margin-top: 0.5em;
    }
  }
  .film-list {
    margin-top: 1em;
    columns: 3 auto;
    text-align: left;
    font-size: xx-small;
  }
}

.achievements-card {
  .expandable-card-header {
    margin-bottom: 10px;

    .subtitle {
      font-size: smaller;
      font-style: italic;
    }
  }

  .profile-achievement-ring {
    color: var(--mol-color-gold);
    font-weight: bold;
    margin-bottom:2.25em;

    &.profile-achievement-ring-incomplete {
      color: #a8a8a8;
    }
  }
}

.membership-required-link {

  color: var(--mol-color-red-carpet);
  background-color: var(--mol-color-red-carpet-background);
  font-size: 10px;

  position: relative;
  display: inline-block;
  border: solid;
  border-width: thin;
  border-color: var(--mol-color-red-carpet);
  border-radius: 6px;
  margin: 2px;
  text-align: center;
  padding: 1px 4px 3px 4px;

  text-decoration: inherit;

  img {
    margin-top: 6px;
    height: 20px;
    vertical-align: top;
  }

  .membership-required-text {
    margin-left: 3px;
    display: inline-block;
  }

  &:hover {
    background-color: var(--mol-color-red-carpet-background);
  }
}

.profile-films-viewed-graphic {
  position: absolute;
  top: 0;
  left: 0;
}
.profile-info-layered-over-graphic {
  position: relative;
}

.percentage-stat {

  visibility: hidden;

  font-family: 'Playpen Sans', cursive, sans-serif;
  color: var(--mol-color-gold-text);
  text-shadow: 0 0 10px var(--bs-body-bg);

  &.no-films-viewed {
    color: var(--bs-tertiary-color);
  }

  &.fade-in {
    visibility: visible;
    animation: fadeIn 0.5s;
  }
}

.placeholder-profile-image {
  height: 100px;
  width: 100px;
  /*background-color: var(--bs-body-color);*/
  background-color: #decc8280;
  opacity: 20%;
  margin-left: auto;
  margin-right: auto;
  border-radius: 50%;
}

.placeholder-profile-name {
  margin: 20px auto 20px auto;

  height: 36px;
  width: 150px;
  /*background-color: var(--bs-body-color);*/
  background-color: #decc8280;
  opacity: 20%;
}

.error-message {
  font-size: large;
  margin-top: 50px;
  margin-bottom: 100px;
}

@keyframes fadeIn {
  0% {opacity: 0%;}
  100% {opacity: 100%;}
}


.ribbon-msg {
  height: 20px;
  img {
    display: block;
    margin: 3px auto 3px auto;
    height: 14px;
    pointer-events: none;
  }
}


.ribbon {
  position: absolute;
  left: -2px;
  top: -2px;
  z-index: 1;
  overflow: hidden;
  width: 75px;
  height: 75px;
  text-align: right;
  /*background-color: lightcyan;*/
}
.ribbon span {
  font-size: 10px;
  font-weight: 500;
  color: white;
  /*text-transform: uppercase;*/
  text-align: center;
  line-height: 20px;
  transform: rotate(-45deg);
  -webkit-transform: rotate(-45deg);
  width: 70px;
  display: block;
  background: var(--mol-color-red-carpet-background);
  background: linear-gradient(var(--mol-color-red-carpet-slightly-faded) 0%, var(--mol-color-red-carpet) 100%);
  box-shadow: 0 3px 10px -5px rgba(0, 0, 0, 1);
  position: absolute;
  top: 8.2px; left: -16.8px;
}
.ribbon span::before {
  content: "";
  position: absolute;
  left: 0px;
  top: 100%;
  z-index: -1;
  border-left:1px solid var(--mol-color-red-carpet);
  border-right: 1px solid transparent;
  border-bottom: 1px solid transparent;
  border-top: 1px solid var(--mol-color-red-carpet);
}
.ribbon span::after {
  content: "";
  position: absolute;
  right: 0px;
  top: 100%;
  z-index: -1;
  border-left: 1px solid transparent;
  border-right: 1px solid var(--mol-color-red-carpet);
  border-bottom: 1px solid transparent;
  border-top: 1px solid var(--mol-color-red-carpet);
}

</style>